import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Input from "../../components/Atomos/Inputs/Input";
import DropDownList from "../../components/Atomos/DropDown/Dropdown";
import NewButton from "../../components/Atomos/Buttons/newButton";
import Close from "../../components/Atomos/Icons/Close";
import Plus2 from "../../components/Atomos/Icons/Plus";
import Modal from "../../components/Atomos/Modals/Modal";
import CloseModal from "../../components/Atomos/Icons/EditMode/CloseModal";
import GenericFileInputButton from "../../components/Atomos/FileInput/GenericFileInputButton";
import { createEmailTemplate, getEmailTemplateById, updateEmailTemplate } from "../../Services/EmailTemplate";
import { getEmailCategories, createEmailCategory } from "../../Services/EmailCategory";
import { uploadImage } from "../../utils/UploadImage";
import { getEventLookupOptions } from "../../Services/Event";
import { IEmailTemplate } from "../../interfaces/models/IEmailTemplate";
import { IEmailCategory } from "../../interfaces/models/IEmailCategory";
import type { EmailType } from "../../Enums/EmailEnum";
import { EmailTypeEnum } from "../../Enums/EmailEnum";
import CustomFromEmailFields from "../SendEmail/CustomFromEmailFields";

const EMAIL_TYPE_OPTIONS = Object.entries(EmailTypeEnum).map(([value, label]) => ({ id: value, name: label }));

const ATTACHMENT_ACCEPT = ".gif,.png,.jpg,.jpeg,.pdf,.xlsx,.docx";
const MAX_FILE_SIZE_MB = 25;
const ENVELOPE_ATTACHMENT_PATH = "email/envelopes/";

type TemplateSource = "beefree" | "html";

export default function AddEnvelope (): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const templateSource = (location.state as { templateSource?: TemplateSource } | null)?.templateSource ?? "beefree";
  const [categories, setCategories] = useState<IEmailCategory[]>([]);
  const [eventOptions, setEventOptions] = useState<{ id: string; name: string }[]>([]);
  const [loadingTemplate, setLoadingTemplate] = useState(isEditing);
  const [loadError, setLoadError] = useState("");
  const [templateToEdit, setTemplateToEdit] = useState<IEmailTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState<Partial<IEmailTemplate>>({
    category: "",
    title: "",
    subject: "",
    sender: "",
    fromEmail: "",
    emailType: undefined,
    emailEventId: "",
    attachment: "",
    templateSource,
  });
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);

  const [newCategoryModalOpen, setNewCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryError, setNewCategoryError] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getEmailCategories().then((data) => {
      if (!cancelled) {
        setCategories(data ?? []);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getEventLookupOptions().then((data) => {
      if (cancelled) {
        return;
      }
      setEventOptions(
        (data ?? []).map((event) => ({
          id: event.id ?? "",
          name: event.title?.trim() ? event.title : (event.id ?? "Untitled"),
        })),
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEditing || !id) {
      setLoadingTemplate(false);
      setLoadError("");
      setTemplateToEdit(null);
      return;
    }

    let cancelled = false;
    setLoadingTemplate(true);
    setLoadError("");

    getEmailTemplateById(id)
      .then((data) => {
        if (cancelled) {
          return;
        }
        if (!data) {
          setLoadError("Could not load envelope information.");
          setTemplateToEdit(null);
          return;
        }

        setTemplateToEdit(data);
        setForm({
          category: data.category ?? "",
          title: data.title ?? "",
          subject: data.subject ?? "",
          sender: data.sender ?? "",
          fromEmail: data.fromEmail ?? "",
          emailType: data.emailType,
          emailEventId: data.emailEventId ?? "",
          attachment: data.attachment ?? "",
          templateSource: data.templateSource ?? templateSource,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError("Could not load envelope information.");
          setTemplateToEdit(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTemplate(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id, isEditing, templateSource]);

  const update = (field: keyof IEmailTemplate, value: string | undefined) => {
    const next = value ?? "";
    setForm((prev) => ({
      ...prev,
      [field]: field === "emailType" ? (next ? (next as EmailType) : undefined) : next,
    }));
    if (errors[field]) {
      setErrors((e) => ({ ...e, [field]: "" }));
    }
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.category?.trim()) {
      next.category = "Category is required.";
    }
    if (!form.emailType) {
      next.emailType = "Type is required.";
    }
    if (!form.title?.trim()) {
      next.title = "Title is required.";
    }
    if (!form.subject?.trim()) {
      next.subject = "Subject is required.";
    }
    if (!form.fromEmail?.trim()) {
      next.fromEmail = "From Email is required.";
    }
    if (!form.sender?.trim()) {
      next.sender = "From is required.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const openNewCategoryModal = () => {
    setNewCategoryName("");
    setNewCategoryError("");
    setNewCategoryModalOpen(true);
  };

  const closeNewCategoryModal = () => {
    setNewCategoryModalOpen(false);
    setNewCategoryName("");
    setNewCategoryError("");
  };

  const handleSaveNewCategory = async () => {
    const name = newCategoryName?.trim();
    if (!name) {
      setNewCategoryError("Name is required.");
      return;
    }
    setSavingCategory(true);
    setNewCategoryError("");
    try {
      const created = await createEmailCategory({ name });
      setCategories((prev) => [...prev, created].filter(Boolean));
      update("category", created?.name ?? name);
      closeNewCategoryModal();
    } catch {
      setNewCategoryError("Could not create category. Please try again.");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleCancel = () => {
    navigate(isEditing && id ? `/email-design/${id}` : "/email-design");
  };

  const handleSaveAndContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    if (isEditing && !templateToEdit) {
      return;
    }
    setSaving(true);
    try {
      let attachmentUrl = form.attachment ?? "";
      if (attachmentFile) {
        const folderId = `envelope`;
        const uploaded = await uploadImage(
          attachmentFile,
          folderId,
          ENVELOPE_ATTACHMENT_PATH
        );
        if (uploaded) {
          attachmentUrl = uploaded;
        }
      }
      if (isEditing && templateToEdit) {
        const updated = await updateEmailTemplate({
          ...templateToEdit,
          title: form.title ?? "",
          category: form.category ?? "",
          subject: form.subject ?? "",
          sender: form.sender ?? "",
          fromEmail: form.fromEmail ?? "",
          emailType: (form.emailType as EmailType) || "",
          attachment: attachmentUrl,
          templateSource: (form.templateSource as TemplateSource) ?? templateToEdit.templateSource,
          emailEventId: form.emailEventId?.trim() || undefined,
        });
        navigate(updated?.id ? `/email-design/${updated.id}` : `/email-design/${id}`);
      } else {
        const payload: IEmailTemplate = {
          title: form.title ?? "",
          category: form.category ?? "",
          subject: form.subject ?? "",
          sender: form.sender ?? "",
          fromEmail: form.fromEmail ?? "",
          emailType: (form.emailType as EmailType) || "",
          attachment: attachmentUrl,
          template: "",
          templateSource,
          emailEventId: form.emailEventId?.trim() || undefined,
          createdDate: Math.floor(Date.now() / 1000),
        };
        const created = await createEmailTemplate(payload);
        if (templateSource === "beefree" && created?.id) {
          navigate(`/email-design/${created.id}/edit`);
        } else if (templateSource === "html" && created?.id) {
          navigate(`/email-design/${created.id}/edit-html`);
        } else {
          navigate("/email-design");
        }
      }
    } catch {
      setErrors((e) => ({ ...e, submit: "Failed to save. Please try again." }));
      setSaving(false);
    }
  };

  const attachmentUrl = form.attachment?.trim() || "";
  const attachmentFilename = attachmentUrl
    ? attachmentUrl.startsWith("http")
      ? attachmentUrl.split("/").filter(Boolean).pop() ?? attachmentUrl
      : attachmentUrl
    : "";
  const attachmentTitle =
    attachmentFilename && /^\d+/.test(attachmentFilename)
      ? attachmentFilename.replace(/^\d+/, "").trim() || attachmentFilename
      : attachmentFilename;

  if (loadingTemplate) {
    return (
      <main className="text-left p-10 bg-white w-full">
        <p className="text-gray-font">Loading envelope information...</p>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="text-left p-10 bg-white w-full">
        <div className="flex flex-col gap-4">
          <p className="text-red-600 font-medium">{loadError}</p>
          <div className="w-fit">
            <NewButton
              type="button"
              onClick={() => navigate("/email-design")}
              text="Back to templates"
              color="neutral"
              content="text"
              size="small"
              style="outlined"
            />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="text-left p-10 bg-white w-full">
      <Modal
        className="!text-Default py-6 px-6 md:py-8 md:px-8 lg:py-10 lg:px-10"
        isModalOpen={newCategoryModalOpen}
        toggleModal={closeNewCategoryModal}
        title="Add New Category"
        titleColor="text-Default"
      >
        <div className="flex flex-col gap-6 min-w-[28rem] sm:min-w-[38rem]">
          <Input
            title="Category Name"
            name="newCategoryName"
            value={newCategoryName}
            onChange={(e) => {
              setNewCategoryName(e.target.value);
              if (newCategoryError) {
                setNewCategoryError("");
              }
            }}
            error={!!newCategoryError}
            errorMsj={newCategoryError}
            required
          />
          <div className="flex flex-col gap-4 w-full sm:flex-row sm:justify-end sm:w-auto">
            <NewButton
              type="button"
              onClick={closeNewCategoryModal}
              content="textIcon"
              size="small"
              style="outlined"
              color="neutral"
              icon={<CloseModal /> as JSX.Element}
              text="Cancel"
              className="w-full sm:w-auto"
            />
            <NewButton
              type="button"
              onClick={handleSaveNewCategory}
              content="textIcon"
              size="small"
              style="filled"
              color="accent"
              icon={<Plus2 /> as JSX.Element}
              text={savingCategory ? "Saving…" : "Add category"}
              disabled={savingCategory}
              className="w-full sm:w-auto"
            />
          </div>
        </div>
      </Modal>

      <h1 className="text-2xl font-medium text-Default mb-6">
        {isEditing ? "Edit Envelope Information" : "Envelope Information"}
      </h1>

      <form onSubmit={handleSaveAndContinue} className="w-full">
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] md:grid-cols-[auto_auto_auto] gap-4 md:gap-6 w-full md:w-fit">
            <div className="flex flex-col gap-2 w-full min-w-0 sm:max-w-[18rem] md:w-[18rem]">
              <DropDownList
                title="Category"
                name="category"
                data={categories.map((c) => ({ id: c.id ?? c.name, name: c.name }))}
                selectedObjst={form.category ?? undefined}
                seter={(val: string | { id: string; name: string } | null) =>
                  update("category", typeof val === "string" ? val : val?.name ?? "")
                }
                required
                error={!!errors.category}
                errormsj={errors.category}
                placeholder="Select category"
              />
              <p className="text-sm text-gray-500">e.g. Accounting, New Feature, ...</p>
            </div>
            <div className="flex flex-col w-fit min-w-0 gap-4">
              <div className="h-5 md:h-6 shrink-0" aria-hidden="true" />
              <button
                type="button"
                onClick={openNewCategoryModal}
                className="flex items-center w-fit shrink-0"
              >
                <NewButton
                  text="New category"
                  icon={<Plus2 /> as JSX.Element}
                  color="accent"
                  content="textIcon"
                  size="medium"
                  style="filled"
                />
              </button>
            </div>
            <div className="flex flex-col gap-2 w-full min-w-0 sm:max-w-[18rem] md:w-[18rem]">
              <DropDownList
                title="Type"
                name="emailType"
                data={EMAIL_TYPE_OPTIONS}
                selectedObjst={EMAIL_TYPE_OPTIONS.find((o) => o.id === form.emailType)?.name ?? undefined}
                seter={(val: string | null) => {
                  const option = EMAIL_TYPE_OPTIONS.find((o) => o.name === val);
                  update("emailType", option?.id ?? "");
                }}
                required
                error={!!errors.emailType}
                errormsj={errors.emailType}
                placeholder="Select type"
              />
              <p className="text-sm text-gray-500">Used to flag un-subscribed members.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-10 gap-6 w-full">
            <div className="w-full min-w-0 md:col-span-6">
              <Input
                title="Title"
                name="title"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                error={!!errors.title}
                errorMsj={errors.title}
                required
              />
            </div>
            <div className="w-full min-w-0 md:col-span-4 gap-2">
              <Input
                title="Subject"
                name="subject"
                required={true}
                value={form.subject}
                onChange={(e) => update("subject", e.target.value)}
                error={!!errors.subject}
                errorMsj={errors.subject}
              />
              <p className="text-sm text-gray-500 -mt-1.5">As it appears on an email subject&apos;s line.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-10 gap-6 w-full">
            <div className="flex flex-col gap-2 w-full min-w-0 md:col-span-6">
              <DropDownList
                title="Event"
                name="emailEventId"
                data={eventOptions}
                selectedObjst={eventOptions.find((o: { id: string; name: string }) => o.id === form.emailEventId)?.name ?? undefined}
                seter={(val: string | null) => {
                  const option = eventOptions.find((o: { id: string; name: string }) => o.name === val);
                  update("emailEventId", option?.id ?? "");
                }}
                error={!!errors.emailEventId}
                errormsj={errors.emailEventId}
                placeholder="Select event"
                internalSearch={true}
              />
              <p className="text-sm text-gray-500">Used to flag attending and un-subscribed members.</p>
            </div>
            <div className="flex flex-col gap-2 w-full min-w-0 md:col-span-4">
              <GenericFileInputButton
                title="Attachment"
                accept={ATTACHMENT_ACCEPT}
                maxSizeMb={MAX_FILE_SIZE_MB}
                onFileSelected={(file) => {
                  setAttachmentFile(file);
                  setErrors((e) => ({ ...e, attachment: "" }));
                  update("attachment", file.name);
                }}
                onFileRemoved={() => {
                  setAttachmentFile(null);
                  update("attachment", "");
                  setErrors((e) => ({ ...e, attachment: "" }));
                }}
                hint={`Allowed: .gif, .png, .jpg, .jpeg, .pdf, .xlsx, .docx. Max ${MAX_FILE_SIZE_MB} MB.`}
                variant="accent"
              />
              {!attachmentFile && attachmentTitle && (
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span>
                    Current attachment:{" "}
                    {attachmentUrl.startsWith("http") ? (
                      <a
                        href={attachmentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-Default underline hover:no-underline"
                      >
                        {attachmentTitle}
                      </a>
                    ) : (
                      attachmentTitle
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => update("attachment", "")}
                    className="text-sm text-[#474F56] underline hover:no-underline"
                  >
                    Remove
                  </button>
                </div>
              )}
              {errors.attachment && (
                <p className="text-sm text-red-600">{errors.attachment}</p>
              )}
            </div>
          </div>

          <CustomFromEmailFields
            layout="add"
            fromEmail={form.fromEmail || ""}
            sender={form.sender || ""}
            errors={{ fromEmail: errors.fromEmail, sender: errors.sender }}
            onChange={({ fromEmail, sender }) => {
              setForm((prev) => ({ ...prev, fromEmail, sender }));
            }}
            onClearErrors={(fields) => {
              setErrors((prev) => {
                const next = { ...prev };
                (fields || ["fromEmail", "sender"]).forEach((field) => {
                  delete next[field];
                });
                return next;
              });
            }}
          />
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mt-8 pt-6">
          <NewButton
            type="button"
            onClick={handleCancel}
            text="Cancel"
            icon={<Close /> as JSX.Element}
            color="neutral"
            content="textIcon"
            size="small"
            style="outlined"
          />
          <button type="submit" disabled={saving} className="flex items-center shrink-0">
            <NewButton
              text={saving ? "Saving…" : isEditing ? "Save changes" : "Save and Continue"}
              color="accent"
              content="textIcon"
              size="small"
              style="filled"
              disabled={saving}
              className="w-full sm:w-auto"
            />
          </button>
        </div>
      </form>
    </main>
  );
}
